// US Payroll Calculation Engine
// Comprehensive gross-to-net calculations for US-based payroll

import { STATE_TAX_CONFIG, US_STATES } from './stateTaxData';

// ============================================================================
// 2024/2025 Federal Tax Constants
// ============================================================================

export const FEDERAL_TAX_BRACKETS_2024 = {
  single: [
    { min: 0, max: 11600, rate: 0.10 },
    { min: 11600, max: 47150, rate: 0.12 },
    { min: 47150, max: 100525, rate: 0.22 },
    { min: 100525, max: 191950, rate: 0.24 },
    { min: 191950, max: 243725, rate: 0.32 },
    { min: 243725, max: 609350, rate: 0.35 },
    { min: 609350, max: Infinity, rate: 0.37 }
  ],
  married: [
    { min: 0, max: 23200, rate: 0.10 },
    { min: 23200, max: 94300, rate: 0.12 },
    { min: 94300, max: 201050, rate: 0.22 },
    { min: 201050, max: 383900, rate: 0.24 },
    { min: 383900, max: 487450, rate: 0.32 },
    { min: 487450, max: 731200, rate: 0.35 },
    { min: 731200, max: Infinity, rate: 0.37 }
  ],
  marriedSeparate: [
    { min: 0, max: 11600, rate: 0.10 },
    { min: 11600, max: 47150, rate: 0.12 },
    { min: 47150, max: 100525, rate: 0.22 },
    { min: 100525, max: 191950, rate: 0.24 },
    { min: 191950, max: 243725, rate: 0.32 },
    { min: 243725, max: 365600, rate: 0.35 },
    { min: 365600, max: Infinity, rate: 0.37 }
  ],
  headOfHousehold: [
    { min: 0, max: 16550, rate: 0.10 },
    { min: 16550, max: 63100, rate: 0.12 },
    { min: 63100, max: 100500, rate: 0.22 },
    { min: 100500, max: 191950, rate: 0.24 },
    { min: 191950, max: 243700, rate: 0.32 },
    { min: 243700, max: 609350, rate: 0.35 },
    { min: 609350, max: Infinity, rate: 0.37 }
  ]
};

// Standard deductions for 2024
export const STANDARD_DEDUCTIONS_2024 = {
  single: 14600,
  married: 29200,
  marriedSeparate: 14600,
  headOfHousehold: 21900
};

// FICA Tax Rates
export const FICA_RATES = {
  socialSecurity: {
    rate: 0.062, // 6.2%
    wageBase: 168600, // 2024 wage base
    employerRate: 0.062
  },
  medicare: {
    rate: 0.0145, // 1.45%
    additionalRate: 0.009, // Additional 0.9% for high earners
    additionalThreshold: 200000, // Single filer threshold
    additionalThresholdMarried: 250000,
    employerRate: 0.0145
  }
};

// FUTA (Federal Unemployment Tax)
export const FUTA_RATES = {
  rate: 0.006, // 0.6% after credit
  wageBase: 7000
};

// ============================================================================
// State Tax Data (Unified - derived from STATE_TAX_CONFIG)
// This ensures calculations use the same data as the UI
// ============================================================================

// Helper function to normalize brackets from STATE_TAX_CONFIG format
function normalizeBrackets(config, filingStatus = 'single') {
  if (!config.brackets) return null;
  
  // If brackets is an object with filing status keys (e.g., { single: [...], married: [...] })
  if (config.brackets[filingStatus]) {
    return config.brackets[filingStatus];
  }
  if (config.brackets.single) {
    return config.brackets.single; // Default to single if requested status not found
  }
  // If brackets is already an array, use as-is
  if (Array.isArray(config.brackets)) {
    return config.brackets;
  }
  return null;
}

// Generate STATE_TAX_DATA from STATE_TAX_CONFIG for calculation use
function generateStateTaxData() {
  const data = {};
  
  for (const [stateCode, config] of Object.entries(STATE_TAX_CONFIG)) {
    data[stateCode] = {
      name: config.name,
      hasStateTax: config.hasStateTax,
      flatRate: config.flatRate || null,
      brackets: normalizeBrackets(config, 'single'),
      standardDeduction: config.standardDeduction || null,
      exemptions: config.exemptions || null,
      additionalRate: config.additionalRate || null,
      additionalThreshold: config.additionalThreshold || null,
      hasLocalTax: config.otherTaxes?.localTaxCommon || false,
      hasSDI: config.otherTaxes?.sdiRequired || false,
      sdiRate: config.otherTaxes?.sdiRate || 0,
      hasPFML: config.otherTaxes?.pfmlRequired || false,
      pfmlRate: config.otherTaxes?.pfmlRate || 0
    };
  }
  
  return data;
}

export const STATE_TAX_DATA = generateStateTaxData();

// Re-export STATE_TAX_CONFIG and US_STATES for components that need detailed info
export { STATE_TAX_CONFIG, US_STATES };

// ============================================================================
// Pay Frequency Constants
// ============================================================================

export const PAY_FREQUENCIES = {
  weekly: { periodsPerYear: 52, label: 'Weekly' },
  biweekly: { periodsPerYear: 26, label: 'Bi-Weekly' },
  semimonthly: { periodsPerYear: 24, label: 'Semi-Monthly' },
  monthly: { periodsPerYear: 12, label: 'Monthly' }
};

// ============================================================================
// Earning Types
// ============================================================================

export const EARNING_TYPES = {
  regular: { code: 'REG', name: 'Regular Pay', taxable: true, overtime: false },
  overtime: { code: 'OT', name: 'Overtime', taxable: true, overtime: true, multiplier: 1.5 },
  doubleTime: { code: 'DT', name: 'Double Time', taxable: true, overtime: true, multiplier: 2.0 },
  holiday: { code: 'HOL', name: 'Holiday Pay', taxable: true, overtime: false },
  vacation: { code: 'VAC', name: 'Vacation Pay', taxable: true, overtime: false },
  sick: { code: 'SICK', name: 'Sick Pay', taxable: true, overtime: false },
  bonus: { code: 'BON', name: 'Bonus', taxable: true, overtime: false, supplemental: true },
  commission: { code: 'COM', name: 'Commission', taxable: true, overtime: false },
  tipsCash: { code: 'TIPC', name: 'Cash Tips', taxable: true, overtime: false },
  tipsCharged: { code: 'TIPCH', name: 'Charged Tips', taxable: true, overtime: false },
  reimbursement: { code: 'REIMB', name: 'Reimbursement', taxable: false, overtime: false },
  severance: { code: 'SEV', name: 'Severance Pay', taxable: true, overtime: false, supplemental: true }
};

// ============================================================================
// Deduction Types
// ============================================================================

export const DEDUCTION_TYPES = {
  // Pre-Tax Deductions (Retirement - NOT FICA exempt)
  traditional401k: { code: '401K', name: '401(k)', preTax: true, ficaExempt: false, category: 'retirement', limit: 23000 },
  '401k': { code: '401K', name: '401(k)', preTax: true, ficaExempt: false, category: 'retirement', limit: 23000 }, // Alias
  traditional403b: { code: '403B', name: '403(b)', preTax: true, ficaExempt: false, category: 'retirement', limit: 23000 },
  traditional457: { code: '457', name: '457 Plan', preTax: true, ficaExempt: false, category: 'retirement', limit: 23000 },
  simpleIRA: { code: 'SIRA', name: 'SIMPLE IRA', preTax: true, ficaExempt: false, category: 'retirement', limit: 16000 },
  
  // Pre-Tax Deductions (Section 125 Cafeteria Plan - FICA exempt)
  hsa: { code: 'HSA', name: 'HSA', preTax: true, ficaExempt: true, category: 'health', limit: 4150 }, // Single limit
  fsa: { code: 'FSA', name: 'FSA', preTax: true, ficaExempt: true, category: 'health', limit: 3200 },
  dcfsa: { code: 'DCFSA', name: 'Dependent Care FSA', preTax: true, ficaExempt: true, category: 'health', limit: 5000 },
  healthInsurance: { code: 'HLTH', name: 'Health Insurance', preTax: true, ficaExempt: true, category: 'health' },
  health: { code: 'HLTH', name: 'Health Insurance', preTax: true, ficaExempt: true, category: 'health' }, // Alias
  dentalInsurance: { code: 'DENT', name: 'Dental Insurance', preTax: true, ficaExempt: true, category: 'health' },
  dental: { code: 'DENT', name: 'Dental Insurance', preTax: true, ficaExempt: true, category: 'health' }, // Alias
  visionInsurance: { code: 'VIS', name: 'Vision Insurance', preTax: true, ficaExempt: true, category: 'health' },
  vision: { code: 'VIS', name: 'Vision Insurance', preTax: true, ficaExempt: true, category: 'health' }, // Alias
  lifeInsurance: { code: 'LIFE', name: 'Life Insurance (Pre-tax)', preTax: true, ficaExempt: true, category: 'insurance' },
  life: { code: 'LIFE', name: 'Life Insurance (Pre-tax)', preTax: true, ficaExempt: true, category: 'insurance' }, // Alias
  commuter: { code: 'COMM', name: 'Commuter Benefits', preTax: true, ficaExempt: false, category: 'other', limit: 315 }, // Per month - NOT FICA exempt
  
  // Post-Tax Deductions
  roth401k: { code: 'R401K', name: 'Roth 401(k)', preTax: false, category: 'retirement', limit: 23000 },
  rothIRA: { code: 'RIRA', name: 'Roth IRA', preTax: false, category: 'retirement', limit: 7000 },
  lifeInsurancePost: { code: 'LIFEP', name: 'Life Insurance (Post-tax)', preTax: false, category: 'insurance' },
  disabilityInsurance: { code: 'DIS', name: 'Disability Insurance', preTax: false, category: 'insurance' },
  
  // Garnishments (Post-Tax, Court-Ordered)
  childSupport: { code: 'CSUP', name: 'Child Support', preTax: false, category: 'garnishment', priority: 1 },
  taxLevy: { code: 'LEVY', name: 'Tax Levy', preTax: false, category: 'garnishment', priority: 2 },
  studentLoan: { code: 'STLN', name: 'Student Loan Garnishment', preTax: false, category: 'garnishment', priority: 3 },
  creditorGarnishment: { code: 'GARN', name: 'Creditor Garnishment', preTax: false, category: 'garnishment', priority: 4 },
  
  // Other
  unionDues: { code: 'UNION', name: 'Union Dues', preTax: false, category: 'other' },
  charityDeduction: { code: 'CHAR', name: 'Charitable Donation', preTax: false, category: 'other' },
  loanRepayment: { code: 'LOAN', name: 'Loan Repayment', preTax: false, category: 'other' }
};

// ============================================================================
// Core Calculation Functions
// ============================================================================

/**
 * Calculate federal income tax using progressive brackets
 * @param {number} annualTaxableWages - Annual taxable wages after pre-tax deductions
 * @param {string} filingStatus - W-4 filing status (single, married, marriedSeparate, headOfHousehold)
 * @param {number} additionalWithholding - Additional withholding per pay period
 * @param {number} payPeriods - Number of pay periods per year
 * @returns {object} Tax calculation details
 */
export function calculateFederalIncomeTax(annualTaxableWages, filingStatus = 'single', additionalWithholding = 0, payPeriods = 26) {
  const brackets = FEDERAL_TAX_BRACKETS_2024[filingStatus] || FEDERAL_TAX_BRACKETS_2024.single;
  const standardDeduction = STANDARD_DEDUCTIONS_2024[filingStatus] || STANDARD_DEDUCTIONS_2024.single;
  
  // Apply standard deduction
  const taxableIncome = Math.max(0, annualTaxableWages - standardDeduction);
  
  let annualTax = 0;
  let remainingIncome = taxableIncome;
  const bracketBreakdown = [];
  
  for (const bracket of brackets) {
    if (remainingIncome <= 0) break;
    
    const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
    const taxInBracket = taxableInBracket * bracket.rate;
    
    if (taxableInBracket > 0) {
      bracketBreakdown.push({
        bracket: `${bracket.rate * 100}%`,
        taxableAmount: taxableInBracket,
        tax: taxInBracket
      });
    }
    
    annualTax += taxInBracket;
    remainingIncome -= taxableInBracket;
  }
  
  const perPeriodTax = annualTax / payPeriods;
  const withAdditional = perPeriodTax + additionalWithholding;
  
  return {
    annualTax: Math.round(annualTax * 100) / 100,
    perPeriodTax: Math.round(perPeriodTax * 100) / 100,
    withAdditional: Math.round(withAdditional * 100) / 100,
    effectiveRate: taxableIncome > 0 ? (annualTax / taxableIncome * 100).toFixed(2) : 0,
    marginalRate: brackets.find(b => taxableIncome <= b.max)?.rate * 100 || 37,
    standardDeduction,
    taxableIncome,
    bracketBreakdown
  };
}

/**
 * Calculate Social Security tax
 * @param {number} grossWages - Gross wages for period
 * @param {number} ytdWages - Year-to-date wages (for wage base limit)
 * @returns {object} SS tax details
 */
export function calculateSocialSecurityTax(grossWages, ytdWages = 0) {
  const { rate, wageBase } = FICA_RATES.socialSecurity;
  
  // Check if already over wage base
  if (ytdWages >= wageBase) {
    return { tax: 0, taxableWages: 0, wageBaseReached: true };
  }
  
  // Calculate taxable wages (up to wage base)
  const remainingWageBase = wageBase - ytdWages;
  const taxableWages = Math.min(grossWages, remainingWageBase);
  const tax = taxableWages * rate;
  
  return {
    tax: Math.round(tax * 100) / 100,
    taxableWages,
    wageBaseReached: (ytdWages + grossWages) >= wageBase,
    rate: rate * 100
  };
}

/**
 * Calculate Medicare tax (including additional Medicare tax)
 * @param {number} grossWages - Gross wages for period
 * @param {number} ytdWages - Year-to-date wages
 * @param {string} filingStatus - Filing status for additional Medicare threshold
 * @returns {object} Medicare tax details
 */
export function calculateMedicareTax(grossWages, ytdWages = 0, filingStatus = 'single') {
  const { rate, additionalRate, additionalThreshold, additionalThresholdMarried } = FICA_RATES.medicare;
  
  const threshold = filingStatus === 'married' ? additionalThresholdMarried : additionalThreshold;
  
  // Base Medicare tax
  const baseTax = grossWages * rate;
  
  // Additional Medicare tax (0.9% on wages over threshold)
  let additionalTax = 0;
  if (ytdWages + grossWages > threshold) {
    const wagesOverThreshold = Math.max(0, ytdWages + grossWages - Math.max(ytdWages, threshold));
    additionalTax = wagesOverThreshold * additionalRate;
  }
  
  return {
    baseTax: Math.round(baseTax * 100) / 100,
    additionalTax: Math.round(additionalTax * 100) / 100,
    totalTax: Math.round((baseTax + additionalTax) * 100) / 100,
    rate: rate * 100,
    additionalRate: additionalRate * 100,
    threshold
  };
}

/**
 * Calculate state income tax
 * @param {number} annualTaxableWages - Annual taxable wages
 * @param {string} stateCode - Two-letter state code
 * @param {string} filingStatus - Filing status
 * @param {number} payPeriods - Pay periods per year
 * @returns {object} State tax details
 */
export function calculateStateIncomeTax(annualTaxableWages, stateCode, filingStatus = 'single', payPeriods = 26) {
  const stateData = STATE_TAX_DATA[stateCode];
  
  if (!stateData || !stateData.hasStateTax) {
    return { tax: 0, perPeriodTax: 0, noStateTax: true, stateName: stateData?.name || 'Unknown' };
  }
  
  let annualTax = 0;
  let effectiveRate = 0;
  
  // Apply state standard deduction if applicable
  let taxableIncome = annualTaxableWages;
  if (stateData.standardDeduction) {
    const deduction = stateData.standardDeduction[filingStatus] || stateData.standardDeduction.single;
    taxableIncome = Math.max(0, annualTaxableWages - deduction);
  }
  
  if (stateData.flatRate) {
    // Flat tax state
    annualTax = taxableIncome * stateData.flatRate;
    effectiveRate = stateData.flatRate * 100;
    
    // Additional tax for high earners (e.g., Massachusetts)
    if (stateData.additionalRate && taxableIncome > stateData.additionalThreshold) {
      annualTax += (taxableIncome - stateData.additionalThreshold) * stateData.additionalRate;
    }
  } else if (stateData.brackets) {
    // Progressive tax state
    let remainingIncome = taxableIncome;
    
    for (const bracket of stateData.brackets) {
      if (remainingIncome <= 0) break;
      
      const taxableInBracket = Math.min(remainingIncome, bracket.max - bracket.min);
      annualTax += taxableInBracket * bracket.rate;
      remainingIncome -= taxableInBracket;
    }
    
    effectiveRate = taxableIncome > 0 ? (annualTax / taxableIncome * 100) : 0;
  }
  
  return {
    stateName: stateData.name,
    annualTax: Math.round(annualTax * 100) / 100,
    perPeriodTax: Math.round((annualTax / payPeriods) * 100) / 100,
    effectiveRate: effectiveRate.toFixed(2),
    hasLocalTax: stateData.hasLocalTax || false,
    hasSDI: stateData.hasSDI || false,
    sdiRate: stateData.sdiRate || 0,
    hasPFML: stateData.hasPFML || false,
    pfmlRate: stateData.pfmlRate || 0
  };
}

/**
 * Calculate local taxes (city/county)
 * @param {number} grossWages - Gross wages
 * @param {string} locality - Locality code
 * @param {number} rate - Local tax rate
 * @returns {object} Local tax details
 */
export function calculateLocalTax(grossWages, locality, rate = 0) {
  const tax = grossWages * rate;
  return {
    locality,
    tax: Math.round(tax * 100) / 100,
    rate: rate * 100
  };
}

/**
 * Calculate pre-tax deductions
 * @param {Array} deductions - Array of deduction objects with type and amount
 * @param {number} grossPay - Gross pay for the period
 * @param {object} ytdDeductions - Year-to-date deduction totals
 * @returns {object} Pre-tax deduction details
 */
export function calculatePreTaxDeductions(deductions = [], grossPay, ytdDeductions = {}) {
  const results = [];
  let totalPreTax = 0;
  let totalFicaExempt = 0; // Track FICA-exempt deductions separately
  
  console.log(`[calculatePreTaxDeductions] Processing ${deductions.length} deductions for gross pay $${grossPay}`);
  
  // Known pre-tax deduction categories for fallback matching
  const preTaxCategories = ['retirement', 'health', 'insurance'];
  const preTaxKeywords = ['401k', 'hsa', 'fsa', 'health', 'dental', 'vision', 'medical', 'life', 'retirement'];
  // FICA-exempt keywords (Section 125 cafeteria plan items)
  const ficaExemptKeywords = ['hsa', 'fsa', 'health', 'dental', 'vision', 'medical', 'life'];
  
  for (const deduction of deductions) {
    console.log(`[calculatePreTaxDeductions] Processing deduction:`, deduction);
    
    let dedType = DEDUCTION_TYPES[deduction.type];
    
    // If not found by exact match, try lowercase
    if (!dedType && deduction.type) {
      dedType = DEDUCTION_TYPES[deduction.type.toLowerCase()];
      if (dedType) console.log(`[calculatePreTaxDeductions] Found via lowercase: ${deduction.type.toLowerCase()}`);
    }
    
    // If still not found, check if it looks like a pre-tax deduction by keyword
    if (!dedType && deduction.type) {
      const typeLower = deduction.type.toLowerCase();
      const looksPreTax = preTaxKeywords.some(kw => typeLower.includes(kw));
      if (looksPreTax) {
        // Determine if FICA-exempt based on keywords
        const isFicaExempt = ficaExemptKeywords.some(kw => typeLower.includes(kw));
        dedType = { 
          code: deduction.type.toUpperCase(), 
          name: deduction.type, 
          preTax: true, 
          ficaExempt: isFicaExempt,
          category: 'other' 
        };
        console.log(`[calculatePreTaxDeductions] Created via keyword match: ${typeLower}, ficaExempt: ${isFicaExempt}`);
      }
    }
    
    // Also check if deduction has explicit preTax flag
    if (!dedType && deduction.preTax === true) {
      dedType = { 
        code: deduction.type?.toUpperCase() || 'OTHER', 
        name: deduction.type || 'Other Pre-Tax', 
        preTax: true, 
        ficaExempt: deduction.ficaExempt || false,
        category: 'other' 
      };
      console.log(`[calculatePreTaxDeductions] Created via preTax flag`);
    }
    
    if (!dedType) {
      console.log(`[calculatePreTaxDeductions] SKIPPED - No matching type found for: ${deduction.type}`);
      continue;
    }
    
    if (!dedType.preTax) {
      console.log(`[calculatePreTaxDeductions] SKIPPED - Not pre-tax: ${deduction.type}`);
      continue;
    }
    
    let amount = deduction.amount;
    
    // Check for percentage-based deductions
    if (deduction.isPercentage) {
      amount = grossPay * (deduction.amount / 100);
    }
    
    // Check annual limits
    if (dedType.limit) {
      const ytdAmount = ytdDeductions[deduction.type] || 0;
      const remainingLimit = dedType.limit - ytdAmount;
      amount = Math.min(amount, Math.max(0, remainingLimit));
    }
    
    amount = Math.round(amount * 100) / 100;
    
    if (amount > 0) {
      const isFicaExempt = dedType.ficaExempt === true;
      results.push({
        type: deduction.type,
        code: dedType.code,
        name: dedType.name,
        amount,
        category: dedType.category,
        ficaExempt: isFicaExempt
      });
      totalPreTax += amount;
      if (isFicaExempt) {
        totalFicaExempt += amount;
      }
    }
  }
  
  return {
    deductions: results,
    total: Math.round(totalPreTax * 100) / 100,
    totalFicaExempt: Math.round(totalFicaExempt * 100) / 100 // FICA-exempt portion (Section 125)
  };
}

/**
 * Calculate post-tax deductions (including garnishments)
 * @param {Array} deductions - Array of deduction objects
 * @param {number} netAfterTax - Net pay after taxes
 * @param {object} ytdDeductions - Year-to-date deductions
 * @returns {object} Post-tax deduction details
 */
export function calculatePostTaxDeductions(deductions = [], netAfterTax, ytdDeductions = {}) {
  const results = [];
  let totalPostTax = 0;
  
  // Sort garnishments by priority
  const sortedDeductions = [...deductions].sort((a, b) => {
    const aType = DEDUCTION_TYPES[a.type];
    const bType = DEDUCTION_TYPES[b.type];
    return (aType?.priority || 99) - (bType?.priority || 99);
  });
  
  let remainingPay = netAfterTax;
  
  for (const deduction of sortedDeductions) {
    const dedType = DEDUCTION_TYPES[deduction.type];
    if (!dedType || dedType.preTax) continue;
    
    let amount = deduction.amount;
    
    // Check for percentage-based deductions
    if (deduction.isPercentage) {
      amount = netAfterTax * (deduction.amount / 100);
    }
    
    // Check annual limits
    if (dedType.limit) {
      const ytdAmount = ytdDeductions[deduction.type] || 0;
      const remainingLimit = dedType.limit - ytdAmount;
      amount = Math.min(amount, Math.max(0, remainingLimit));
    }
    
    // Garnishment limits (can't take more than 25% of disposable earnings for most garnishments)
    if (dedType.category === 'garnishment' && deduction.type !== 'childSupport') {
      const maxGarnishment = remainingPay * 0.25;
      amount = Math.min(amount, maxGarnishment);
    }
    
    // Child support can take up to 50-65% depending on circumstances
    if (deduction.type === 'childSupport') {
      const maxChildSupport = remainingPay * (deduction.maxPercentage || 0.50);
      amount = Math.min(amount, maxChildSupport);
    }
    
    // Ensure we don't go below minimum wage equivalent
    amount = Math.min(amount, Math.max(0, remainingPay));
    amount = Math.round(amount * 100) / 100;
    
    if (amount > 0) {
      results.push({
        type: deduction.type,
        code: dedType.code,
        name: dedType.name,
        amount,
        category: dedType.category
      });
      totalPostTax += amount;
      remainingPay -= amount;
    }
  }
  
  return {
    deductions: results,
    total: Math.round(totalPostTax * 100) / 100
  };
}

/**
 * Calculate employer taxes
 * @param {number} grossWages - Gross wages
 * @param {number} ytdWages - Year-to-date wages
 * @param {string} stateCode - State code for SUI
 * @param {number} suiRate - State unemployment insurance rate
 * @returns {object} Employer tax details
 */
export function calculateEmployerTaxes(grossWages, ytdWages = 0, stateCode = '', suiRate = 0.027) {
  // Employer Social Security
  const { wageBase } = FICA_RATES.socialSecurity;
  const remainingSS = Math.max(0, wageBase - ytdWages);
  const ssTaxableWages = Math.min(grossWages, remainingSS);
  const employerSS = ssTaxableWages * FICA_RATES.socialSecurity.employerRate;
  
  // Employer Medicare
  const employerMedicare = grossWages * FICA_RATES.medicare.employerRate;
  
  // FUTA
  const remainingFUTA = Math.max(0, FUTA_RATES.wageBase - ytdWages);
  const futaTaxableWages = Math.min(grossWages, remainingFUTA);
  const futa = futaTaxableWages * FUTA_RATES.rate;
  
  // SUI (State Unemployment)
  const suiWageBase = 7000; // Most states, but varies
  const remainingSUI = Math.max(0, suiWageBase - ytdWages);
  const suiTaxableWages = Math.min(grossWages, remainingSUI);
  const sui = suiTaxableWages * suiRate;
  
  return {
    socialSecurity: Math.round(employerSS * 100) / 100,
    medicare: Math.round(employerMedicare * 100) / 100,
    futa: Math.round(futa * 100) / 100,
    sui: Math.round(sui * 100) / 100,
    total: Math.round((employerSS + employerMedicare + futa + sui) * 100) / 100
  };
}

// ============================================================================
// Main Gross-to-Net Calculation
// ============================================================================

/**
 * Complete gross-to-net payroll calculation
 * @param {object} params - Calculation parameters
 * @returns {object} Complete payroll calculation breakdown
 */
export function calculateGrossToNet({
  // Employee info
  employeeId,
  employeeName,
  
  // Earnings
  earnings = [],
  
  // Pay information
  payFrequency = 'biweekly',
  
  // W-4 information
  filingStatus = 'single',
  federalAllowances = 0,
  additionalFederalWithholding = 0,
  
  // State tax info
  workState = '',
  residentState = '',
  stateAllowances = 0,
  additionalStateWithholding = 0,
  
  // Local tax info
  localTaxRate = 0,
  locality = '',
  
  // Deductions
  preTaxDeductions = [],
  postTaxDeductions = [],
  
  // YTD information
  ytdGrossWages = 0,
  ytdFederalTax = 0,
  ytdStateTax = 0,
  ytdSocialSecurity = 0,
  ytdMedicare = 0,
  ytdPreTaxDeductions = {},
  ytdPostTaxDeductions = {},
  
  // Employer info
  suiRate = 0.027
}) {
  const payPeriods = PAY_FREQUENCIES[payFrequency]?.periodsPerYear || 26;
  
  // ============================================
  // Step 1: Calculate Gross Pay
  // ============================================
  let grossPay = 0;
  const earningsBreakdown = [];
  
  for (const earning of earnings) {
    const earnType = EARNING_TYPES[earning.type] || { name: earning.type, taxable: true };
    let amount = earning.amount || 0;
    
    // Handle hourly calculations
    if (earning.hours && earning.rate) {
      const multiplier = earnType.multiplier || 1;
      amount = earning.hours * earning.rate * multiplier;
    }
    
    amount = Math.round(amount * 100) / 100;
    
    earningsBreakdown.push({
      type: earning.type,
      code: earnType.code || earning.type.toUpperCase(),
      name: earnType.name,
      hours: earning.hours || null,
      rate: earning.rate || null,
      amount,
      taxable: earnType.taxable
    });
    
    grossPay += amount;
  }
  
  grossPay = Math.round(grossPay * 100) / 100;
  
  // ============================================
  // Step 2: Calculate Pre-Tax Deductions
  // ============================================
  const preTaxResult = calculatePreTaxDeductions(preTaxDeductions, grossPay, ytdPreTaxDeductions);
  
  // taxableGross = Gross - ALL pre-tax deductions (for income tax purposes)
  const taxableGross = grossPay - preTaxResult.total;
  const annualTaxableWages = taxableGross * payPeriods;
  
  // ficaTaxableGross = Gross - ONLY Section 125 (FICA-exempt) deductions
  // 401(k) contributions are NOT exempt from FICA, only from income tax
  const ficaTaxableGross = grossPay - preTaxResult.totalFicaExempt;
  
  // Debug logging for tax calculation
  console.log(`[Payroll Calc] Gross: $${grossPay}, Pre-Tax Deductions: $${preTaxResult.total} (FICA-exempt: $${preTaxResult.totalFicaExempt})`);
  console.log(`[Payroll Calc] Taxable Gross (for income tax): $${taxableGross}, FICA Taxable: $${ficaTaxableGross}`);
  console.log(`[Payroll Calc] Annual Taxable Wages: $${annualTaxableWages}, Pay Periods: ${payPeriods}`);
  console.log(`[Payroll Calc] Pre-Tax Deductions Breakdown:`, preTaxResult.deductions);
  
  // ============================================
  // Step 3: Calculate Federal Income Tax
  // ============================================
  console.log(`[Payroll Calc] Calling calculateFederalIncomeTax with annualTaxableWages: $${annualTaxableWages}, filingStatus: ${filingStatus}`);
  
  const federalTax = calculateFederalIncomeTax(
    annualTaxableWages,
    filingStatus,
    additionalFederalWithholding,
    payPeriods
  );
  
  console.log(`[Payroll Calc] Federal Tax Result: perPeriod=$${federalTax.withAdditional}, annual=$${federalTax.annualTax}, taxableIncome=$${federalTax.taxableIncome}`);
  
  // ============================================
  // Step 4: Calculate FICA Taxes (on FICA-taxable wages only)
  // 401(k) does NOT reduce FICA taxes, only HSA/FSA/Health insurance does
  // ============================================
  const ytdFicaTaxable = ytdGrossWages - (ytdPreTaxDeductions.totalFicaExempt || 0);
  const socialSecurity = calculateSocialSecurityTax(ficaTaxableGross, ytdFicaTaxable);
  const medicare = calculateMedicareTax(ficaTaxableGross, ytdFicaTaxable, filingStatus);
  
  console.log(`[Payroll Calc] FICA: SS=$${socialSecurity.tax} (6.2% of $${ficaTaxableGross}), Medicare=$${medicare.totalTax} (1.45% of $${ficaTaxableGross})`);
  
  // ============================================
  // Step 5: Calculate State Tax
  // ============================================
  const stateTax = calculateStateIncomeTax(annualTaxableWages, workState || residentState, filingStatus, payPeriods);
  stateTax.perPeriodTax += additionalStateWithholding;
  
  // Handle SDI if applicable (same base as FICA)
  let sdiTax = 0;
  if (stateTax.hasSDI && stateTax.sdiRate) {
    sdiTax = Math.round(ficaTaxableGross * stateTax.sdiRate * 100) / 100;
  }
  
  // Handle PFML if applicable (same base as FICA)
  let pfmlTax = 0;
  if (stateTax.hasPFML && stateTax.pfmlRate) {
    pfmlTax = Math.round(ficaTaxableGross * stateTax.pfmlRate * 100) / 100;
  }
  
  // ============================================
  // Step 6: Calculate Local Tax
  // ============================================
  const localTax = calculateLocalTax(taxableGross, locality, localTaxRate);
  
  // ============================================
  // Step 7: Calculate Net After Tax
  // ============================================
  const totalTaxes = 
    federalTax.withAdditional + 
    socialSecurity.tax + 
    medicare.totalTax + 
    stateTax.perPeriodTax + 
    sdiTax +
    pfmlTax +
    localTax.tax;
  
  const netAfterTax = taxableGross - totalTaxes;
  
  // ============================================
  // Step 8: Calculate Post-Tax Deductions
  // ============================================
  const postTaxResult = calculatePostTaxDeductions(postTaxDeductions, netAfterTax, ytdPostTaxDeductions);
  
  // ============================================
  // Step 9: Calculate Net Pay
  // ============================================
  const netPay = netAfterTax - postTaxResult.total;
  
  // ============================================
  // Step 10: Calculate Employer Taxes
  // ============================================
  const employerTaxes = calculateEmployerTaxes(taxableGross, ytdGrossWages, workState, suiRate);
  
  // ============================================
  // Build Result Object
  // ============================================
  return {
    employeeId,
    employeeName,
    payPeriod: {
      frequency: payFrequency,
      periodsPerYear: payPeriods
    },
    
    // Earnings
    earnings: {
      breakdown: earningsBreakdown,
      grossPay
    },
    
    // Pre-Tax Deductions
    preTaxDeductions: {
      ...preTaxResult,
      taxableGross
    },
    
    // Taxes
    taxes: {
      federal: {
        ...federalTax,
        amount: federalTax.withAdditional
      },
      socialSecurity: {
        ...socialSecurity,
        amount: socialSecurity.tax
      },
      medicare: {
        ...medicare,
        amount: medicare.totalTax
      },
      state: {
        ...stateTax,
        amount: stateTax.perPeriodTax
      },
      sdi: sdiTax,
      pfml: pfmlTax,
      local: {
        ...localTax,
        amount: localTax.tax
      },
      totalTaxes: Math.round(totalTaxes * 100) / 100
    },
    
    // Post-Tax Deductions
    postTaxDeductions: postTaxResult,
    
    // Net Pay
    netPay: Math.round(netPay * 100) / 100,
    
    // Employer Costs
    employerTaxes,
    totalEmployerCost: Math.round((grossPay + employerTaxes.total) * 100) / 100,
    
    // Summary
    summary: {
      grossPay,
      preTaxDeductions: preTaxResult.total,
      taxableGross,
      totalTaxes: Math.round(totalTaxes * 100) / 100,
      postTaxDeductions: postTaxResult.total,
      netPay: Math.round(netPay * 100) / 100
    },
    
    // Calculation metadata
    calculatedAt: new Date().toISOString()
  };
}

// ============================================================================
// Supplemental Wage Tax Calculation
// ============================================================================

/**
 * Calculate taxes on supplemental wages (bonuses, commissions)
 * Uses flat 22% federal rate (or 37% for amounts over $1M)
 */
export function calculateSupplementalWageTax(amount, ytdSupplemental = 0, stateCode = '') {
  // Federal supplemental rate
  let federalRate = 0.22;
  if (ytdSupplemental + amount > 1000000) {
    // Amount over $1M taxed at 37%
    const underMillion = Math.max(0, 1000000 - ytdSupplemental);
    const overMillion = amount - underMillion;
    const federalTax = (underMillion * 0.22) + (overMillion * 0.37);
    federalRate = federalTax / amount;
  }
  
  const federalTax = amount * federalRate;
  
  // FICA on supplemental wages
  const ssTax = amount * FICA_RATES.socialSecurity.rate;
  const medicareTax = amount * FICA_RATES.medicare.rate;
  
  // State supplemental rates vary - using flat rate if available
  const stateData = STATE_TAX_DATA[stateCode];
  let stateTax = 0;
  if (stateData?.hasStateTax) {
    stateTax = amount * (stateData.flatRate || 0.05); // Default 5% estimate
  }
  
  return {
    federal: Math.round(federalTax * 100) / 100,
    socialSecurity: Math.round(ssTax * 100) / 100,
    medicare: Math.round(medicareTax * 100) / 100,
    state: Math.round(stateTax * 100) / 100,
    total: Math.round((federalTax + ssTax + medicareTax + stateTax) * 100) / 100,
    netAmount: Math.round((amount - federalTax - ssTax - medicareTax - stateTax) * 100) / 100
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert annual salary to per-period amount
 */
export function annualToPerPeriod(annualAmount, payFrequency = 'biweekly') {
  const periods = PAY_FREQUENCIES[payFrequency]?.periodsPerYear || 26;
  return Math.round((annualAmount / periods) * 100) / 100;
}

/**
 * Convert per-period amount to annual
 */
export function perPeriodToAnnual(perPeriodAmount, payFrequency = 'biweekly') {
  const periods = PAY_FREQUENCIES[payFrequency]?.periodsPerYear || 26;
  return Math.round((perPeriodAmount * periods) * 100) / 100;
}

/**
 * Calculate hourly rate from annual salary
 */
export function annualToHourly(annualSalary, hoursPerWeek = 40) {
  return Math.round((annualSalary / 52 / hoursPerWeek) * 100) / 100;
}

/**
 * Calculate annual salary from hourly rate
 */
export function hourlyToAnnual(hourlyRate, hoursPerWeek = 40) {
  return Math.round(hourlyRate * hoursPerWeek * 52 * 100) / 100;
}

/**
 * Format currency for display
 */
export function formatUSDCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
}

/**
 * Get all states with tax info
 */
export function getStatesForDropdown() {
  return Object.entries(STATE_TAX_DATA).map(([code, data]) => ({
    code,
    name: data.name,
    hasStateTax: data.hasStateTax
  })).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Validate W-4 data
 */
export function validateW4Data(w4Data) {
  const errors = [];
  
  if (!w4Data.filingStatus) {
    errors.push('Filing status is required');
  }
  
  if (w4Data.additionalWithholding && w4Data.additionalWithholding < 0) {
    errors.push('Additional withholding cannot be negative');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Calculate year-to-date totals
 */
export function calculateYTDTotals(payslips = []) {
  return payslips.reduce((ytd, payslip) => ({
    grossWages: ytd.grossWages + (payslip.earnings?.grossPay || 0),
    federalTax: ytd.federalTax + (payslip.taxes?.federal?.amount || 0),
    stateTax: ytd.stateTax + (payslip.taxes?.state?.amount || 0),
    socialSecurity: ytd.socialSecurity + (payslip.taxes?.socialSecurity?.amount || 0),
    medicare: ytd.medicare + (payslip.taxes?.medicare?.amount || 0),
    preTaxDeductions: ytd.preTaxDeductions + (payslip.preTaxDeductions?.total || 0),
    postTaxDeductions: ytd.postTaxDeductions + (payslip.postTaxDeductions?.total || 0),
    netPay: ytd.netPay + (payslip.netPay || 0)
  }), {
    grossWages: 0,
    federalTax: 0,
    stateTax: 0,
    socialSecurity: 0,
    medicare: 0,
    preTaxDeductions: 0,
    postTaxDeductions: 0,
    netPay: 0
  });
}

export default {
  calculateGrossToNet,
  calculateFederalIncomeTax,
  calculateSocialSecurityTax,
  calculateMedicareTax,
  calculateStateIncomeTax,
  calculateLocalTax,
  calculatePreTaxDeductions,
  calculatePostTaxDeductions,
  calculateEmployerTaxes,
  calculateSupplementalWageTax,
  FEDERAL_TAX_BRACKETS_2024,
  STATE_TAX_DATA,
  PAY_FREQUENCIES,
  EARNING_TYPES,
  DEDUCTION_TYPES,
  FICA_RATES,
  formatUSDCurrency,
  getStatesForDropdown
};
